import React, { useEffect, useState } from "react";
import { fetchMonthlyTurnover } from "../api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import PropTypes from 'prop-types';
import { CircularProgress, Box } from "@mui/material";
import { getMonthDays } from "../utils/month";
import { useTranslation } from "react-i18next";
import { useCustomerCurrency, useCustomerLocale } from "../context/CustomerSettingsContext";
import { parseLocalDateTime } from "../utils/localDateTime";

const WorkcellMonthlyTurnoverChart = ({ date, workcellId, onClick, activeIndex, onHover, loading, onLoadingChange }) => {
    const [data, setData] = useState([]);
    const [internalLoading, setInternalLoading] = useState(false);
    const { t } = useTranslation();
    const locale = useCustomerLocale();
    const { currency, currencySymbol, formatCurrency } = useCustomerCurrency();
    useEffect(() => {
        // If required inputs are missing, clear data and inform parent we're not loading
        if (!date || !workcellId) {
            setData([]);
            setInternalLoading(false);
            if (typeof onLoadingChange === 'function') onLoadingChange(false);
            return;
        }

        let mounted = true;
        setInternalLoading(true);
        const callParent = typeof onLoadingChange === 'function';
        if (callParent) onLoadingChange(true);

        fetchMonthlyTurnover(date)
            .then((apiData) => {
                if (!mounted) return;
                if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
                    setData([]);
                    return;
                }

                // Filter for this workcell
                const filtered = apiData.filter(row => String(row.workcell_id) === String(workcellId));
                if (filtered.length === 0) {
                    setData([]);
                    return;
                }

                // Build full month data
                const days = getMonthDays(date);
                const fullMonthData = days.map((day) => ({
                    day,
                    operation_turnover: 0,
                    hourly_turnover: 0,
                    total_turnover: 0,
                }));

                filtered.forEach((entry) => {
                    const index = fullMonthData.findIndex((d) => d.day === entry.day);
                    if (index !== -1) {
                        fullMonthData[index].operation_turnover = entry.operation_turnover;
                        fullMonthData[index].hourly_turnover = entry.hourly_turnover;
                        fullMonthData[index].total_turnover = entry.total_turnover;
                    }
                });

                setData(fullMonthData);
            })
            .catch(() => {
                if (!mounted) return;
                setData([]);
            })
            .finally(() => {
                if (!mounted) return;
                setInternalLoading(false);
                if (callParent) onLoadingChange(false);
            });

        return () => { mounted = false; };
    }, [date, workcellId, onLoadingChange]);

    // Use the loading prop if provided, otherwise fallback to internal loading
    const isLoading = typeof loading === 'boolean' ? loading : internalLoading;

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <CircularProgress />
            </Box>
        );
    }

    if (data.length === 0)
        return <div>{t("workcell.monthlyOverview.turnover.noData")}</div>;
    // Custom tick for month name (only show for first day of month)
    const MonthTick = (props) => {
        const { x, y, payload, index } = props;
        const date = parseLocalDateTime(payload.value);
        // Show month name only for the first day or when month changes
        const isFirst = index === 0;
        const prevDate = index > 0 ? parseLocalDateTime(data[index - 1].day) : null;
        const isMonthStart = isFirst || (prevDate && prevDate.getMonth() !== date.getMonth());
        return isMonthStart ? (
            <g>
                <text x={x} y={y + 16} textAnchor="middle" fontSize={12} fill="#888">
                    {date.toLocaleString(locale, { month: 'long' })}
                </text>
            </g>
        ) : null;
    };

    return (
        <div>
            <ResponsiveContainer width="100%" height={300}>
                <BarChart
                    data={data}
                    syncId="workcellCharts"
                >
                    {/* Main X axis: days */}
                    <XAxis
                        dataKey="day"
                        tickFormatter={tick => parseLocalDateTime(tick)?.getDate() || ''}
                        height={30}
                        axisLine={true}
                        tickLine={true}
                    />
                    {/* Secondary X axis: month name */}
                    <XAxis
                        dataKey="day"
                        axisLine={false}
                        tickLine={false}
                        height={20}
                        xAxisId="month"
                        tick={MonthTick}
                        interval={0}
                        allowDataOverflow={true}
                        orientation="bottom"
                    />
                    <YAxis
                        label={{
                            value: t("workcell.monthlyOverview.turnover.axisLabel", { currency: currencySymbol }),
                            angle: -90,
                            position: "insideLeft",
                            style: { textAnchor: "middle" }
                        }}
                    />
                    <Tooltip
                        content={({ active, payload, label }) => {
                            if (!active || !payload || payload.length === 0) return null;
                            // Find the correct data object for this day
                            const d = data.find(row => row.day === label);
                            // Color codes
                            const opColor = '#ff9800'; // orange
                            const hrColor = '#1976d2'; // blue
                            const totalColor = '#222'; // black
                            return (
                                <div style={{ background: '#fff', border: '1px solid #ccc', padding: 10 }}>
                                    <div><b>{t("common.date")}:</b> {label}</div>
                                    <div><span style={{ display: 'inline-block', width: 12, height: 12, background: opColor, marginRight: 6, borderRadius: 2 }}></span><b>{t("workcell.monthlyOverview.turnover.tooltip.operation")}:</b> {d?.operation_turnover != null ? formatCurrency(d.operation_turnover, { fallback: t("common.na") }) : t("common.na")}</div>
                                    <div><span style={{ display: 'inline-block', width: 12, height: 12, background: hrColor, marginRight: 6, borderRadius: 2 }}></span><b>{t("workcell.monthlyOverview.turnover.tooltip.hourly")}:</b> {d?.hourly_turnover != null ? formatCurrency(d.hourly_turnover, { fallback: t("common.na") }) : t("common.na")}</div>
                                    <div><span style={{ display: 'inline-block', width: 12, height: 12, background: totalColor, marginRight: 6, borderRadius: 2 }}></span><b>{t("workcell.monthlyOverview.turnover.tooltip.total")}:</b> {d?.total_turnover != null ? formatCurrency(d.total_turnover, { fallback: t("common.na") }) : t("common.na")}</div>
                                </div>
                            );
                        }}
                    />
                    <Legend />
                    <Bar
                        dataKey="operation_turnover"
                        stackId="a"
                        fill="#ff9800"
                        name={t("workcell.monthlyOverview.turnover.legend.operation", { currency })}
                        cursor="pointer"
                        onClick={(data, index) => {
                            if (onClick) onClick(data.day);
                        }}
                    />
                    <Bar
                        dataKey="hourly_turnover"
                        stackId="a"
                        fill="#1976d2"
                        name={t("workcell.monthlyOverview.turnover.legend.hourly", { currency })}
                        cursor="pointer"
                        onClick={(data, index) => {
                            if (onClick) onClick(data.day);
                        }}
                    />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

WorkcellMonthlyTurnoverChart.propTypes = {
  date: PropTypes.string.isRequired,
  workcellId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onClick: PropTypes.func,
  activeIndex: PropTypes.number,
  onHover: PropTypes.func,
    loading: PropTypes.bool,
    onLoadingChange: PropTypes.func,
};

export default WorkcellMonthlyTurnoverChart;
